import { Head, Link, router } from "@inertiajs/react"
import { FileText, Plus } from "lucide-react"
import { useMemo, useState } from "react"
import { AppLayout } from "../../components/layout/AppLayout"
import { Button, buttonVariants } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Input } from "../../components/ui/input"
import { cn } from "../../lib/cn"
import {
  EMISSAO_STATUS_LABELS,
  EmissaoStatusBadge,
  type EmissaoStatus
} from "../../features/invoices/emissaoStatus"

type InvoiceRow = {
  id: number
  codigo_sap: string
  cr: string
  company_id: number | null
  company_name: string
  client_id: number
  client_name: string
  municipio: string
  periodo_inicio: string
  periodo_fim: string
  valor: number
  tributos: number
  total_liquido: number
  emissao_status: EmissaoStatus | string
  omie_numero_nfs?: string | null
  omie_numero_rps?: string | null
}

type InvoicesIndexProps = {
  invoices: InvoiceRow[]
  clients: Array<{ id: number; name: string; municipio: string; company_id?: number | null }>
  companies: Array<{ id: number; name: string; cnpj: string }>
}

function money(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value)
}

function dateRange(start: string, finish: string) {
  if (!start || !finish) return "-"
  const format = (date: string) => new Date(`${date}T00:00:00`).toLocaleDateString("pt-BR")
  return `${format(start)} - ${format(finish)}`
}

export default function InvoicesIndex({ invoices, clients, companies }: InvoicesIndexProps) {
  const [search, setSearch] = useState("")
  const [companyId, setCompanyId] = useState("")
  const [clientId, setClientId] = useState("")
  const [periodoInicio, setPeriodoInicio] = useState("")
  const [periodoFim, setPeriodoFim] = useState("")
  const [emissaoStatus, setEmissaoStatus] = useState("")
  const [activeTab, setActiveTab] = useState<"pronta_para_emitir" | "emissao" | "emitida" | "rascunho">("pronta_para_emitir")

  const statusFromTab = (tab: "pronta_para_emitir" | "emissao" | "emitida" | "rascunho") => {
    switch (tab) {
      case "pronta_para_emitir":
        return ["pronta_para_emitir"]
      case "emissao":
        return ["emissao", "enviada_para_emitir"] // legado
      case "emitida":
        return ["emitida"]
      case "rascunho":
        return ["rascunho"]
      default:
        return []
    }
  }

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const query = search.trim().toLowerCase()
      const matchSearch =
        !query ||
        invoice.codigo_sap.toLowerCase().includes(query) ||
        invoice.cr.toLowerCase().includes(query) ||
        invoice.client_name.toLowerCase().includes(query) ||
        invoice.company_name.toLowerCase().includes(query)

      const matchCompany = !companyId || String(invoice.company_id ?? "") === companyId
      const matchClient = !clientId || String(invoice.client_id) === clientId
      const matchStart = !periodoInicio || invoice.periodo_inicio >= periodoInicio
      const matchEnd = !periodoFim || invoice.periodo_fim <= periodoFim
      const matchStatus = !emissaoStatus || invoice.emissao_status === emissaoStatus
      const tabStatuses = statusFromTab(activeTab)
      const matchTab = tabStatuses.includes(invoice.emissao_status)
      return matchSearch && matchCompany && matchClient && matchStart && matchEnd && matchStatus && matchTab
    })
  }, [invoices, search, companyId, clientId, periodoInicio, periodoFim, emissaoStatus, activeTab])

  const tabCounts = useMemo(() => {
    const base = { pronta_para_emitir: 0, emissao: 0, emitida: 0, rascunho: 0 }
    invoices.forEach((inv) => {
      if (inv.emissao_status === "pronta_para_emitir") base.pronta_para_emitir += 1
      else if (inv.emissao_status === "emissao" || inv.emissao_status === "enviada_para_emitir") base.emissao += 1
      else if (inv.emissao_status === "emitida") base.emitida += 1
      else if (inv.emissao_status === "rascunho") base.rascunho += 1
    })
    return base
  }, [invoices])

  const clientsNoFiltro = useMemo(() => {
    if (!companyId) return clients
    return clients.filter((c) => c.company_id != null && String(c.company_id) === companyId)
  }, [clients, companyId])

  const totals = useMemo(() => {
    return filteredInvoices.reduce(
      (acc, invoice) => {
        acc.valor += invoice.valor
        acc.tributos += invoice.tributos
        acc.total_liquido += invoice.total_liquido
        return acc
      },
      { valor: 0, tributos: 0, total_liquido: 0 }
    )
  }, [filteredInvoices])

  function enviarTodosProntosDaAba() {
    const ids = filteredInvoices.map((i) => i.id)
    if (ids.length === 0) return
    router.post(
      "/notas-fiscais/enviar-prontas-omie",
      { invoice_ids: ids },
      { preserveScroll: true }
    )
  }

  return (
    <>
      <Head title="Notas Fiscais" />
      <AppLayout>
        <div className="p-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-white">Notas Fiscais</h1>
              <p className="text-slate-400">Gerencie as notas fiscais cadastradas no sistema.</p>
            </div>
            <div className="flex items-center gap-2">
              <Link href="/notas-fiscais/importacao" className={cn(buttonVariants({ variant: "outline" }), "border-slate-700 bg-transparent")}>
                Importar lote
              </Link>
              <Button asChild>
                <Link href="/notas-fiscais/nova">
                  <Plus className="mr-2 h-4 w-4" />
                  Nova Nota Fiscal
                </Link>
              </Button>
            </div>
          </div>

          <Card className="mb-6 border-slate-800 bg-slate-900">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { key: "pronta_para_emitir" as const, label: "Pronto pra emitir", count: tabCounts.pronta_para_emitir },
                  { key: "emissao" as const, label: "Emissão", count: tabCounts.emissao },
                  { key: "emitida" as const, label: "Emitidos", count: tabCounts.emitida },
                  { key: "rascunho" as const, label: "Rascunho", count: tabCounts.rascunho }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`rounded-md border px-3 py-1.5 text-sm ${
                      activeTab === tab.key
                        ? "border-sky-600 bg-sky-900/40 text-sky-100"
                        : "border-slate-700 bg-slate-950 text-slate-300 hover:bg-slate-800"
                    }`}
                  >
                    {tab.label} <span className="ml-1 text-xs opacity-80">({tab.count})</span>
                  </button>
                ))}
                {activeTab === "pronta_para_emitir" && (
                  <Button
                    type="button"
                    className="ml-auto"
                    disabled={filteredInvoices.length === 0}
                    onClick={enviarTodosProntosDaAba}
                  >
                    Enviar todos da aba
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6 border-slate-800 bg-slate-900">
            <CardHeader>
              <CardTitle className="text-base">Filtros</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <Input placeholder="Código SAP, CR, Cliente..." value={search} onChange={(e) => setSearch(e.target.value)} />
              <select
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value)
                  setClientId("")
                }}
                className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              >
                <option value="">Todas as empresas</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              >
                <option value="">{companyId ? "Todos os clientes desta empresa" : "Todos os clientes"}</option>
                {clientsNoFiltro.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
              <select
                value={emissaoStatus}
                onChange={(e) => setEmissaoStatus(e.target.value)}
                className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
              >
                <option value="">Todos os status</option>
                {(Object.keys(EMISSAO_STATUS_LABELS) as EmissaoStatus[]).map((key) => (
                  <option key={key} value={key}>
                    {EMISSAO_STATUS_LABELS[key]}
                  </option>
                ))}
              </select>
              <Input type="date" value={periodoInicio} onChange={(e) => setPeriodoInicio(e.target.value)} />
              <Input type="date" value={periodoFim} onChange={(e) => setPeriodoFim(e.target.value)} />
            </CardContent>
          </Card>

          <Card className="mb-6 border-slate-800 bg-slate-900">
            <CardContent className="grid grid-cols-2 gap-4 p-4 md:grid-cols-3 md:max-w-2xl">
              <div><p className="text-xs text-slate-400">Valor</p><p className="text-sm font-semibold">{money(totals.valor)}</p></div>
              <div><p className="text-xs text-slate-400">Tributos</p><p className="text-sm font-semibold">{money(totals.tributos)}</p></div>
              <div>
                <p className="text-xs text-slate-400">Total da nota (líquido)</p>
                <p className="text-sm font-semibold text-emerald-400">{money(totals.total_liquido)}</p>
              </div>
            </CardContent>
          </Card>

          <div className="mb-3 flex items-center gap-2 text-sm text-slate-400">
            <FileText className="h-4 w-4" />
            <span>
              {filteredInvoices.length} {filteredInvoices.length === 1 ? "nota fiscal encontrada" : "notas fiscais encontradas"}
            </span>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-800 bg-slate-900">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-800 bg-slate-900/70 text-slate-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Código SAP</th>
                  <th className="px-4 py-3 font-medium">CR</th>
                  <th className="px-4 py-3 font-medium">Empresa</th>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Município</th>
                  <th className="px-4 py-3 font-medium">Período</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">N. Nota</th>
                  <th className="px-4 py-3 font-medium">N. RPS</th>
                  <th className="px-4 py-3 text-right font-medium">Valor</th>
                  <th className="px-4 py-3 text-right font-medium">Tributos</th>
                  <th className="px-4 py-3 text-right font-medium">Total da nota</th>
                  <th className="px-4 py-3 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-400" colSpan={13}>
                      Nenhuma nota fiscal cadastrada.
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-slate-800/80 last:border-b-0">
                      <td className="px-4 py-3 text-slate-100">{invoice.codigo_sap}</td>
                      <td className="px-4 py-3 text-slate-200">{invoice.cr}</td>
                      <td className="px-4 py-3 text-slate-200">{invoice.company_name}</td>
                      <td className="px-4 py-3 text-slate-200">{invoice.client_name}</td>
                      <td className="px-4 py-3 text-slate-200">{invoice.municipio}</td>
                      <td className="px-4 py-3 text-slate-200">{dateRange(invoice.periodo_inicio, invoice.periodo_fim)}</td>
                      <td className="px-4 py-3 text-slate-200">
                        <EmissaoStatusBadge status={invoice.emissao_status} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-200">{invoice.omie_numero_nfs || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-200">{invoice.omie_numero_rps || "—"}</td>
                      <td className="px-4 py-3 text-right text-slate-200">{money(invoice.valor)}</td>
                      <td className="px-4 py-3 text-right text-slate-200">{money(invoice.tributos)}</td>
                      <td className="px-4 py-3 text-right font-medium text-emerald-300">
                        {money(invoice.total_liquido)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <Button asChild variant="outline" className="border-slate-700 bg-transparent">
                            <Link href={`/notas-fiscais/${invoice.id}`}>Ver</Link>
                          </Button>
                          <Button asChild variant="outline" className="border-slate-700 bg-transparent">
                            <Link href={`/notas-fiscais/${invoice.id}/editar`}>Editar</Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </AppLayout>
    </>
  )
}
